import clsx from 'clsx'
import React from 'react'
import { Checkbox as AriaCheckbox } from 'react-aria-components'

import { Icon } from '#components/Icon'

import type { IProps } from './interfaces'
import styles from './styles.module.scss'

/**
 * The kit's checkbox without react-hook-form: a controlled `isSelected` / `onChange` pair for state
 * that lives in a component (a table row, a filter). `Checkbox` wraps it for forms.
 */
export const CheckboxBase = ({
  label,
  isSelected,
  onChange,
  isIndeterminate = false,
  isDisabled = false,
  isInvalid = false,
  description,
  errorMessage,
  'aria-label': ariaLabel,
  size = 'md',
  className,
  'data-testid': testId,
}: IProps): React.JSX.Element => {
  const hasContent = Boolean(label || description || (isInvalid && errorMessage))

  return (
    <AriaCheckbox
      className={clsx(styles.checkbox, size === 'sm' && styles.small, className)}
      isSelected={isSelected}
      isIndeterminate={isIndeterminate}
      onChange={onChange}
      isDisabled={isDisabled}
      isInvalid={isInvalid}
      aria-label={ariaLabel}
      data-testid={testId}
    >
      <div className={styles.box}>
        {isIndeterminate ? (
          <span className={styles.dash} aria-hidden="true" />
        ) : (
          <Icon
            name="check"
            color="var(--color-input-checked-content)"
            width="14"
            height="14"
            className={styles.checkIcon}
          />
        )}
      </div>
      {hasContent && (
        <div className={styles.content}>
          {label && <span className={styles.label}>{label}</span>}
          {description && <span className={styles.description}>{description}</span>}
          {isInvalid && errorMessage && (
            <span className={styles.error} role="alert" data-testid={testId ? `${testId}-error` : undefined}>
              {errorMessage}
            </span>
          )}
        </div>
      )}
    </AriaCheckbox>
  )
}
